/**
 * UI-only session hint for the marketing site.
 * Keep name/domain helpers in sync with `packages/website/lib/webapp-session-flag.ts`.
 */
import { websiteUrl } from '../env';

export const SESSION_FLAG_COOKIE_PREFIX = 'll_has_session';
export const SESSION_FLAG_COOKIE_VALUE = '1';
const MAX_AGE_SECONDS = 2592000; // 30 days

const PUBLIC_SUFFIX_PARENTS = new Set(['vercel.app']);

export function hostnameOf(urlOrHost: string): string {
    try {
        const trimmed = urlOrHost.trim();
        const withProto = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
        return new URL(withProto).hostname.toLowerCase();
    } catch {
        return '';
    }
}

function slugHost(host: string): string {
    return host.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
}

export function isLocalHost(host: string): boolean {
    return (
        host === 'localhost'
        || host.endsWith('.localhost')
        || host === '127.0.0.1'
        || host === '[::1]'
        || host === '::1'
    );
}

export function registrableDomain(host: string): string | undefined {
    if (!host || isLocalHost(host)) return undefined;
    const labels = host.split('.');
    if (labels.length < 2) return undefined;
    const base = labels.slice(-2).join('.');
    if (PUBLIC_SUFFIX_PARENTS.has(base)) return undefined;
    return base;
}

export function sessionFlagCookieName(webappUrl: string): string {
    const host = hostnameOf(webappUrl);
    return `${SESSION_FLAG_COOKIE_PREFIX}_${slugHost(host) || 'unknown'}`;
}

/** Shared parent Domain, or undefined for host-only (localhost / no common eTLD+1). */
export function sessionFlagCookieDomain(webappUrl: string, siteUrl: string): string | undefined {
    const webappHost = hostnameOf(webappUrl);
    const websiteHost = hostnameOf(siteUrl);
    if (isLocalHost(webappHost) || isLocalHost(websiteHost)) return undefined;
    const webappBase = registrableDomain(webappHost);
    const websiteBase = registrableDomain(websiteHost);
    if (!webappBase || webappBase !== websiteBase) return undefined;
    return `.${webappBase}`;
}

function cookieAttributeString(maxAge: number): string {
    const name = sessionFlagCookieName(window.location.origin);
    const domain = sessionFlagCookieDomain(
        window.location.origin,
        websiteUrl || 'http://localhost:3002'
    );
    const parts = [
        `${name}=${maxAge > 0 ? SESSION_FLAG_COOKIE_VALUE : ''}`,
        'Path=/',
        `Max-Age=${maxAge}`,
        'SameSite=Lax'
    ];
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    return parts.join('; ');
}

export function setSessionFlagCookie(): void {
    if (typeof document === 'undefined') return;
    document.cookie = cookieAttributeString(MAX_AGE_SECONDS);
}

export function clearSessionFlagCookie(): void {
    if (typeof document === 'undefined') return;
    document.cookie = cookieAttributeString(0);
}
